import {createContext, useContext, useRef} from 'react'
import type {PropsWithChildren} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createLineSelectionSlice, type LineSelectionSlice} from './slices/line-selection'
import {
  createDraggingAddCommentButtonSlice,
  type DraggingAddCommentButtonSlice,
} from './slices/dragging-add-comment-button'

type DiffStoreState = DraggingAddCommentButtonSlice & LineSelectionSlice

type DiffStore = ReturnType<typeof createDiffStore>

const DiffStoreContext = createContext<DiffStore | null>(null)

export function useDiffStore<T>(selector: (state: DiffStoreState) => T): T {
  const store = useContext(DiffStoreContext)
  if (!store) throw new Error('Missing DiffStoreProvider in the tree')

  return useStore(store, selector)
}

export const DiffStoreProvider = ({children, pathDigest}: PropsWithChildren<{pathDigest: string}>) => {
  const storeRef = useRef<DiffStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createDiffStore({
      pathDigest,
    })
  }

  return <DiffStoreContext.Provider value={storeRef.current}>{children}</DiffStoreContext.Provider>
}

const createDiffStore = (initState: {pathDigest: string}) =>
  createStore<DiffStoreState>()(
    devtools(
      (...a) => ({
        ...createLineSelectionSlice()(...a),
        ...createDraggingAddCommentButtonSlice()(...a),
      }),
      {name: `diff-store-${initState.pathDigest}`},
    ),
  )

try{ DiffStoreContext.displayName ||= 'DiffStoreContext' } catch {}
try{ DiffStoreProvider.displayName ||= 'DiffStoreProvider' } catch {}